import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Box,
  InputLabel, FormControl, Select, MenuItem,
  Autocomplete, Alert, CircularProgress
} from '@mui/material';
import { FolderTree } from 'lucide-react';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';
import { useDispatch } from 'react-redux';
import { createAdminSubCategory, updateAdminSubCategory } from '../redux/slices/catalogSlice';
import CatalogSeoFields from './CatalogSeoFields';

const SubCategoryModal = ({ open, onClose, subCategory, categories, onSuccess }) => {
  const dispatch = useDispatch();
  const isEdit = !!subCategory;
  
  const [formData, setFormData] = useState({
    sub_category_name: '',
    category_id: '',
    subcategory_status: 'Activated',
    image: '', seo_title: '', seo_description: '', description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (subCategory && open) {
      setFormData({
        sub_category_name: subCategory.sub_category_name || '',
        category_id: subCategory.category_id || subCategory.categories?._id || '',
        subcategory_status: subCategory.subcategory_status || 'Activated',
        image: subCategory.image || '',
        seo_title: subCategory.seo_title || '',
        seo_description: subCategory.seo_description || '',
        description: typeof subCategory.description === 'string' ? subCategory.description : '',
      });
    } else if (open) {
      setFormData({ 
        sub_category_name: '', 
        category_id: '', 
        subcategory_status: 'Activated',
        image: '', seo_title: '', seo_description: '', description: '',
      });
    }
    setError('');
  }, [subCategory, open]);

  const handleChange = (e) => {
    const value = e.target.name === 'category_id' || e.target.name === 'subcategory_status'
      ? e.target.value
      : e.target.value.trimStart();
    setFormData({ ...formData, [e.target.name]: value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sub_category_name.trim()) {
      setError('Sub category name is required');
      return;
    }
    if (!formData.category_id) {
      setError('Parent category is required');
      return;
    }
    
    setLoading(true);
    try {
      const trimmedData = {
        ...formData,
        sub_category_name: formData.sub_category_name.trim(),
        image: formData.image.trim(),
        seo_title: formData.seo_title.trim(),
        seo_description: formData.seo_description.trim(),
        description: formData.description.trim(),
      };
      if (isEdit) {
        await dispatch(updateAdminSubCategory({ id: subCategory._id, data: trimmedData })).unwrap();
      } else {
        await dispatch(createAdminSubCategory(trimmedData)).unwrap();
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminDialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <AdminDialogHeader
        title={isEdit ? 'Edit Sub Category' : 'Create New Sub Category'}
        subtitle={isEdit ? 'Update subcategory and parent category association' : 'Add a refined classification under a main category'}
        icon={FolderTree}
        iconColor="primary"
        onClose={!loading ? onClose : undefined}
      />
      
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <AdminDialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
            <TextField
              label="Sub Category Name"
              name="sub_category_name"
              value={formData.sub_category_name}
              onChange={handleChange}
              fullWidth
              required
              placeholder="e.g. Smartphones, Men T-Shirts, CNC Tools"
            />
            
            <Autocomplete
              options={categories || []}
              getOptionLabel={(option) => option.category_name || ''}
              value={categories?.find((cat) => cat._id === formData.category_id) || null}
              onChange={(event, newValue) => {
                setFormData({ ...formData, category_id: newValue ? newValue._id : '' });
                if (error) setError('');
              }}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Category"
                  required
                  error={!!error && !formData.category_id}
                />
              )}
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="subcategory_status"
                value={formData.subcategory_status}
                label="Status"
                onChange={handleChange}
              >
                <MenuItem value="Activated">Activated</MenuItem>
                <MenuItem value="Deactivated">Deactivated</MenuItem>
              </Select>
            </FormControl>

            <CatalogSeoFields
              entityLabel="sub-category"
              value={{
                image: formData.image,
                title: formData.seo_title,
                metaDescription: formData.seo_description,
                content: formData.description,
              }}
              onChange={(p) => setFormData((f) => ({
                ...f,
                ...(p.image !== undefined ? { image: p.image } : {}),
                ...(p.title !== undefined ? { seo_title: p.title } : {}),
                ...(p.metaDescription !== undefined ? { seo_description: p.metaDescription } : {}),
                ...(p.content !== undefined ? { description: p.content } : {}),
              }))}
            />
          </Box>
        </AdminDialogContent>
        
        <AdminDialogActions>
          <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
          >
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Sub Category'}
          </Button>
        </AdminDialogActions>
      </form>
    </AdminDialog>
  );
};

export default SubCategoryModal;

